import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download } from "lucide-react";
import { PageHeader, EmptyState } from "@/components/Layout";
import { Button, ErrorNote, inputClass } from "@/components/ui";
import { api, errorMessage } from "@/lib/api";
import { date, money, signClass } from "@/lib/format";
import type { CompareCurrencyTotal, CompareRow } from "@/lib/types";

/*
 * Confronto fra clienti per periodo. Ogni riga è nella valuta dei cicli del
 * cliente (conto prop); solo i totali sono in valuta di reporting, con il tasso
 * accanto e marcato quando è assunto.
 */

type Preset = "month" | "30d" | "year" | "all";

const dayInput = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function presetRange(p: Preset): { from: string; to: string } {
  const now = new Date();
  const to = dayInput(now);
  if (p === "month") return { from: dayInput(new Date(now.getFullYear(), now.getMonth(), 1)), to };
  if (p === "30d") return { from: dayInput(new Date(now.getTime() - 29 * 86_400_000)), to };
  if (p === "year") return { from: dayInput(new Date(now.getFullYear(), 0, 1)), to };
  return { from: "2020-01-01", to };
}

/** Giorni interi in ora locale: dall'inizio del primo alla fine dell'ultimo. */
function periodIso(from: string, to: string) {
  const [fy, fm, fd] = from.split("-").map(Number);
  const [ty, tm, td] = to.split("-").map(Number);
  return { from: new Date(fy!, fm! - 1, fd!, 0, 0, 0, 0).toISOString(), to: new Date(ty!, tm! - 1, td!, 23, 59, 59, 999).toISOString() };
}

const PRESETS: { key: Preset; label: string }[] = [
  { key: "month", label: "Mese corrente" },
  { key: "30d", label: "Ultimi 30 giorni" },
  { key: "year", label: "Da inizio anno" },
  { key: "all", label: "Tutto" },
];

export function Compare() {
  const [range, setRange] = useState(() => presetRange("month"));
  const [exportError, setExportError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const valid = !!range.from && !!range.to && range.from <= range.to;
  const iso = valid ? periodIso(range.from, range.to) : null;

  const q = useQuery({
    queryKey: ["compare", iso?.from, iso?.to],
    queryFn: () => api.compare.get(iso!.from, iso!.to),
    enabled: !!iso,
    placeholderData: (prev) => prev,
  });

  const exportCsv = async () => {
    if (!iso) return;
    setExportError(null);
    setExporting(true);
    try {
      await api.compare.downloadCsv(iso.from, iso.to);
    } catch (e) {
      setExportError(errorMessage(e));
    } finally {
      setExporting(false);
    }
  };

  const data = q.data;
  return (
    <>
      <PageHeader
        title="Confronto"
        subtitle="Risultati per cliente nel periodo: righe nella valuta dei cicli, totali in valuta di reporting"
        actions={
          <Button onClick={() => void exportCsv()} disabled={!data || exporting || data.rows.length === 0}>
            <Download className="mr-1.5 inline h-4 w-4" aria-hidden />
            {exporting ? "Esporto…" : "Esporta CSV"}
          </Button>
        }
      />

      <div className="flex flex-wrap items-end gap-3 px-6 pt-4">
        <div className="flex flex-wrap gap-1" role="group" aria-label="Periodo">
          {PRESETS.map((p) => (
            <Button key={p.key} size="sm" variant="ghost" onClick={() => setRange(presetRange(p.key))}>
              {p.label}
            </Button>
          ))}
        </div>
        <label className="text-xs text-faint">
          Dal
          <input type="date" value={range.from} max={range.to} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} className={`${inputClass} mt-1`} />
        </label>
        <label className="text-xs text-faint">
          Al
          <input type="date" value={range.to} min={range.from} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} className={`${inputClass} mt-1`} />
        </label>
        {!valid && <p className="text-xs text-loss">La data di fine viene prima di quella di inizio.</p>}
      </div>
      {exportError && <div className="px-6 pt-2"><ErrorNote>{exportError}</ErrorNote></div>}
      {q.error && <div className="px-6 pt-2"><ErrorNote>{errorMessage(q.error)}</ErrorNote></div>}

      {!data ? (
        q.isLoading && <p className="px-6 py-6 text-sm text-muted">Carico…</p>
      ) : data.rows.length === 0 ? (
        <EmptyState title="Nessun ciclo da confrontare" hint="Apri una fase per un cliente: dal suo primo ciclo compare qui, con fee, recuperato e netto nel periodo scelto." />
      ) : (
        <div className={`transition-opacity ${q.isFetching ? "opacity-70" : ""}`}>
          <div className="overflow-x-auto px-6 py-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line text-left text-xs text-faint">
                  <th className="py-2 pr-4 font-medium">Cliente</th>
                  <th className="py-2 pr-4 text-right font-medium" title="Cicli aperti e chiusi nel periodo">Cicli aperti / chiusi</th>
                  <th className="py-2 pr-4 text-right font-medium">Fee pagate</th>
                  <th className="py-2 pr-4 text-right font-medium" title="P&L della gamba broker delle coppie chiuse nel periodo, in valuta prop">Recuperato</th>
                  <th className="py-2 pr-4 text-right font-medium" title="Recuperato + payout − fee − rettifiche">Netto</th>
                  <th className="py-2 pr-4 text-right font-medium">Operazioni</th>
                  <th className="py-2 font-medium">Ciclo corrente</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <Row key={`${r.clientId}-${r.currency}`} row={r} />
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-line text-xs text-muted">
                  <td className="py-2 pr-4">Totale in {data.reportingCurrency}{data.totals.estimated ? " · stima" : ""}</td>
                  <td className="py-2 pr-4 text-right tabular-nums">{data.totals.cyclesOpened} / {data.totals.cyclesClosed}</td>
                  <td className="py-2 pr-4 text-right tabular-nums text-ink">{total(data.totals.feesPaid, data.reportingCurrency, data.totals.estimated)}</td>
                  <td className="py-2 pr-4 text-right tabular-nums text-ink">{total(data.totals.recovered, data.reportingCurrency, data.totals.estimated)}</td>
                  <td className={`py-2 pr-4 text-right font-medium tabular-nums ${data.totals.net === null ? "text-muted" : signClass(data.totals.net)}`}>
                    {total(data.totals.net, data.reportingCurrency, data.totals.estimated)}
                  </td>
                  <td className="py-2 pr-4 text-right tabular-nums">{data.totals.operations}</td>
                  <td className="py-2">{data.totals.activeCycles} attivi</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <section className="mx-6 mb-6 rounded-card border border-line p-4 text-sm" aria-label="Conversione in valuta di reporting">
            <p className="text-xs text-faint">Conversione in {data.reportingCurrency}, alla data di fine periodo</p>
            <p className="mt-1 text-xs text-faint">
              Tutto il periodo si converte al tasso della data di fine. Su periodi lunghi il totale può differire dalla somma dei tassi congelati sui
              singoli movimenti del registro: la differenza viene da lì. Per la contabilità fa fede il registro.
            </p>
            <ul className="mt-2 space-y-1">
              {data.byCurrency.map((b) => (
                <RateLine key={b.currency} b={b} reporting={data.reportingCurrency} />
              ))}
            </ul>
            {data.totals.missingRates.length > 0 && (
              <p className="mt-2 text-xs text-loss">
                Manca il tasso {data.totals.missingRates.map((c) => `${c}/${data.reportingCurrency}`).join(", ")}: i totali in {data.reportingCurrency} non si calcolano,
                invece di sommare a tasso 1.
              </p>
            )}
          </section>
        </div>
      )}
    </>
  );
}

function total(v: number | null, currency: string, estimated: boolean) {
  if (v === null) return <span title="Un tasso o una riga mancante: il totale non è calcolabile">—</span>;
  return `${estimated ? "≈ " : ""}${money(v, currency)}`;
}

function Row({ row: r }: { row: CompareRow }) {
  const approx = (on: boolean) => (on ? "≈ " : "");
  const title = r.notes.length ? r.notes.join("\n") : undefined;
  return (
    <tr className="border-b border-line/60 align-top">
      <td className="py-2 pr-4">
        <span className="text-ink">{r.clientName}</span> <span className="text-xs text-faint">{r.currency}</span>
        {r.notes.length > 0 && (
          <p className="text-xs text-warn" title={title}>
            {r.notes[0]}
            {r.notes.length > 1 ? ` (+${r.notes.length - 1})` : ""}
          </p>
        )}
      </td>
      <td className="py-2 pr-4 text-right tabular-nums">{r.cyclesOpened} / {r.cyclesClosed}</td>
      <td className="py-2 pr-4 text-right tabular-nums">{approx(r.estimatedParts.fees)}{money(r.feesPaid, r.currency)}</td>
      <td className={`py-2 pr-4 text-right tabular-nums ${r.recovered === null ? "text-muted" : signClass(r.recovered)}`}>
        {r.recovered === null ? <span title={title}>non calcolabile</span> : `${approx(r.estimatedParts.recovered)}${money(r.recovered, r.currency)}`}
      </td>
      <td className={`py-2 pr-4 text-right font-medium tabular-nums ${r.net === null ? "text-muted" : signClass(r.net)}`}>
        {r.net === null ? "—" : `${approx(r.estimated)}${money(r.net, r.currency)}`}
        {r.payouts !== 0 && <p className="text-xs font-normal text-faint">payout {money(r.payouts, r.currency)}</p>}
        {r.adjustments !== 0 && <p className="text-xs font-normal text-faint">rettifiche {money(r.adjustments, r.currency)}</p>}
      </td>
      <td className="py-2 pr-4 text-right tabular-nums">{r.operations}</td>
      <td className="py-2 text-muted">
        {r.currentCycleDays === null ? "nessuno attivo" : `da ${r.currentCycleDays} ${r.currentCycleDays === 1 ? "giorno" : "giorni"}`}
        {r.currentCycleOpenedAt && <p className="text-xs text-faint">aperto il {date(r.currentCycleOpenedAt)}</p>}
      </td>
    </tr>
  );
}

function RateLine({ b, reporting }: { b: CompareCurrencyTotal; reporting: string }) {
  const same = b.currency === reporting;
  return (
    <li className="flex flex-wrap items-baseline justify-between gap-x-4">
      <span>
        Netto {b.net === null ? "—" : money(b.net, b.currency)}
        {!same && (
          <span className="text-muted">
            {" "}
            × {b.rate === null ? "tasso mancante" : b.rate.toLocaleString("it-IT", { maximumFractionDigits: 6 })}
            {b.rate !== null && ` ${b.currency}/${reporting}`}
          </span>
        )}
      </span>
      <span className="tabular-nums">
        {b.reporting.net === null ? "—" : `${b.rateAssumed ? "≈ " : ""}${money(b.reporting.net, reporting)}`}
        {!same && b.rate !== null && (
          <span className={`ml-2 text-xs ${b.rateAssumed ? "text-warn" : "text-faint"}`}>
            {b.rateAssumed ? "tasso BCE, stima" : b.rateSource === "BROKER_FEED" ? "tasso del feed" : b.rateSource}
            {b.rateValidFor ? ` del ${date(b.rateValidFor)}` : ""}
          </span>
        )}
      </span>
    </li>
  );
}
